/**
 * The Event Calendar's month grid as week rows of bars — the layout behind EventCalendar.
 *
 * The Figma (Rocky-Marketing, the marketer home's calendar) draws a multi-day event as ONE bar
 * across the days it covers, its name once ("2026 Mega Week - MFG Symposium" over 13–15 Oct),
 * and a single-day event as a chip inside its day. So the month is laid out a week at a time:
 *
 *   - each event is cut to the week ({@link layoutWeek}): a segment from its first day in the week
 *     to its last, flagged when it carries on before or after (the design has no frame for that;
 *     the convention is a square edge on the cut side);
 *   - segments are packed into lanes, the rows of a week: longest-starting-first into the first lane
 *     free from its first day, so a long event stays on one row across its days and short ones fill
 *     the gaps. Every lane is shown — a busy week is simply taller — rather than folding the rest
 *     into "+N more" (product decision: everything on the calendar at a glance).
 *
 * Pure: dates in, layout out, no DOM — and here rather than in the component because the coverage
 * config measures only `.ts`.
 */
import type { Event } from '../client/EventCalendarClient';

/** Days since the epoch (local), so dates compare without their time part. */
export function dayIndex(d: Date): number {
  return Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / 86_400_000);
}

/** An event's first and last day, or undefined when it has no start (it cannot be placed). */
export function eventDays(e: Event): { start: number; end: number } | undefined {
  if (!e.startDate) return undefined;
  const start = dayIndex(e.startDate);
  // An end before the start is a data error; treat the event as its start day rather than drop it.
  const end = e.endDate ? Math.max(start, dayIndex(e.endDate)) : start;
  return { start, end };
}

/** One event's piece of one week. Columns are 0 (Monday) to 6 (Sunday). */
export interface Segment {
  event: Event;
  startCol: number;
  /** Days covered in this week, at least 1. */
  span: number;
  lane: number;
  /** The event began before this week (the bar's left edge is a cut, not its start). */
  continuesBefore: boolean;
  /** The event goes on after this week. */
  continuesAfter: boolean;
}

export interface WeekLayout {
  segments: Segment[];
  /** Lanes in use: the week's rows of bars. */
  lanes: number;
}

/**
 * Lays out one week, Monday `weekStart` (a {@link dayIndex}) through Sunday.
 *
 * Order: earlier first; on the same day, the longer event first (so it takes the top lane and the
 * short ones go under it); then by name, then id, so the layout does not change between renders.
 */
export function layoutWeek(weekStart: number, events: readonly Event[]): WeekLayout {
  const weekEnd = weekStart + 6;
  const pieces = events
    .map((event) => ({ event, days: eventDays(event) }))
    .filter((p): p is { event: Event; days: { start: number; end: number } } =>
      !!p.days && p.days.end >= weekStart && p.days.start <= weekEnd,
    )
    .map(({ event, days }) => {
      const from = Math.max(days.start, weekStart);
      const to = Math.min(days.end, weekEnd);
      return {
        event,
        startCol: from - weekStart,
        span: to - from + 1,
        length: days.end - days.start,
        continuesBefore: days.start < weekStart,
        continuesAfter: days.end > weekEnd,
      };
    })
    .sort(
      (a, b) =>
        a.startCol - b.startCol ||
        b.length - a.length ||
        (a.event.eventName ?? '').localeCompare(b.event.eventName ?? '') ||
        (a.event.eventId ?? '').localeCompare(b.event.eventId ?? ''),
    );

  // laneEnds[l] = the last column lane l is taken up to.
  const laneEnds: number[] = [];
  const segments: Segment[] = [];
  for (const p of pieces) {
    let lane = laneEnds.findIndex((end) => end < p.startCol);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = p.startCol + p.span - 1;
    segments.push({
      event: p.event,
      startCol: p.startCol,
      span: p.span,
      lane,
      continuesBefore: p.continuesBefore,
      continuesAfter: p.continuesAfter,
    });
  }
  return { segments, lanes: laneEnds.length };
}
